import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatDivider } from '@angular/material/divider';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, startWith, tap } from 'rxjs/operators';
import { ProjectIntegrationService } from '../../services/project-integration.service';
import { AccountService } from '../../account/account.service';
import { ProjectIntegration, ProviderType, TestConnectionResult } from '../../models/project-integration.model';
import { ProviderBoardSectionComponent } from './provider-board-section/provider-board-section.component';
import { createFormDirtyTracker } from '../../shared/forms/dirty-tracker';

const MAX_BOARDS_PER_PROVIDER = 25;
// Backend-@Size: boardUrl ist auf 500 Zeichen begrenzt, nicht URL_MAX.
const BOARD_URL_MAX = 500;
const BOARD_URL_PATTERN = /^https:\/\/.+/;
const ALLOWED_PROVIDERS: ProviderType[] = ['JIRA', 'REDMINE'];

export interface ProviderState {
  boardUrls: FormArray<FormControl<string>>;
  integrationIds: WritableSignal<(string | null)[]>;
  testResults: WritableSignal<(TestConnectionResult | null)[]>;
  isTesting: WritableSignal<boolean[]>;
}

@Component({
  selector: 'app-project-integration-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    TranslatePipe,
    MatButton,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatIcon,
    MatProgressSpinner,
    MatDivider,
    ProviderBoardSectionComponent,
  ],
  templateUrl: './project-integration-form.component.html',
  styleUrl: './project-integration-form.component.scss',
})
export class ProjectIntegrationFormComponent implements OnChanges {
  private integrationService = inject(ProjectIntegrationService);
  private accountService = inject(AccountService);
  private fb = inject(NonNullableFormBuilder);
  private destroyRef = inject(DestroyRef);

  @Input({ required: true }) projectId!: string;
  @Input() integrations: ProjectIntegration[] = [];
  @Output() saved = new EventEmitter<ProjectIntegration>();

  protected readonly MAX_BOARDS = MAX_BOARDS_PER_PROVIDER;

  isSaving = signal(false);
  saveError = signal(false);

  readonly hasJiraToken = toSignal<boolean | null>(this.accountService.getProfile().pipe(map((p) => p.hasJiraToken)), {
    initialValue: null,
  });

  readonly providerStates: ReadonlyMap<ProviderType, ProviderState> = new Map([
    ['JIRA', ProjectIntegrationFormComponent.createState(this.fb)],
    ['REDMINE', ProjectIntegrationFormComponent.createState(this.fb)],
  ]);

  form = this.fb.group({
    selectedProviders: this.fb.control<ProviderType[]>([]),
    jiraBoardUrls: this.stateOf('JIRA').boardUrls,
    redmineBoardUrls: this.stateOf('REDMINE').boardUrls,
  });

  readonly selectedProviders = toSignal(
    this.form.controls.selectedProviders.valueChanges.pipe(startWith(this.form.controls.selectedProviders.value ?? [])),
    {
      requireSync: true,
    },
  );

  private readonly dirtyTracker = createFormDirtyTracker(this.form);
  readonly isDirty = this.dirtyTracker.isDirty;

  // Backward-compatible getters kept for spec and template access
  get jiraBoardUrls(): FormArray<FormControl<string>> {
    return this.stateOf('JIRA').boardUrls;
  }
  get redmineBoardUrls(): FormArray<FormControl<string>> {
    return this.stateOf('REDMINE').boardUrls;
  }
  get jiraIntegrationIds(): WritableSignal<(string | null)[]> {
    return this.stateOf('JIRA').integrationIds;
  }
  get jiraTestResults(): WritableSignal<(TestConnectionResult | null)[]> {
    return this.stateOf('JIRA').testResults;
  }
  get jiraIsTesting(): WritableSignal<boolean[]> {
    return this.stateOf('JIRA').isTesting;
  }
  get redmineIntegrationIds(): WritableSignal<(string | null)[]> {
    return this.stateOf('REDMINE').integrationIds;
  }
  get redmineTestResults(): WritableSignal<(TestConnectionResult | null)[]> {
    return this.stateOf('REDMINE').testResults;
  }
  get redmineIsTesting(): WritableSignal<boolean[]> {
    return this.stateOf('REDMINE').isTesting;
  }

  private static createState(fb: NonNullableFormBuilder): ProviderState {
    return {
      boardUrls: fb.array<FormControl<string>>([]),
      integrationIds: signal<(string | null)[]>([]),
      testResults: signal<(TestConnectionResult | null)[]>([]),
      isTesting: signal<boolean[]>([]),
    };
  }

  private stateOf(provider: ProviderType): ProviderState {
    return this.providerStates.get(provider)!;
  }

  ngOnChanges(): void {
    this.saveError.set(false);
    this.initializeFromIntegrations();
    this.dirtyTracker.reset();
  }

  private initializeFromIntegrations(): void {
    const jiraIntegrations = this.integrations.filter((i) => i.providerType === 'JIRA');
    const redmineIntegrations = this.integrations.filter((i) => i.providerType === 'REDMINE');

    const selectedProviders: ProviderType[] = [];
    if (jiraIntegrations.length > 0) selectedProviders.push('JIRA');
    if (redmineIntegrations.length > 0) selectedProviders.push('REDMINE');
    if (selectedProviders.length === 0) selectedProviders.push('JIRA');

    this.form.controls.selectedProviders.setValue(selectedProviders);
    this.initializeProvider('JIRA', jiraIntegrations);
    this.initializeProvider('REDMINE', redmineIntegrations);
    this.syncProviderArrayStates(selectedProviders);
  }

  private initializeProvider(provider: ProviderType, existing: ProjectIntegration[]): void {
    const state = this.stateOf(provider);
    state.boardUrls.clear();

    const source: (ProjectIntegration | null)[] = existing.length > 0 ? existing : [null];
    const ids: (string | null)[] = [];
    const testResults: (TestConnectionResult | null)[] = [];
    const testingStates: boolean[] = [];

    for (const integration of source) {
      const ctrl = this.fb.control(integration?.boardUrl ?? '', [
        Validators.required,
        Validators.maxLength(BOARD_URL_MAX),
        Validators.pattern(BOARD_URL_PATTERN),
      ]);
      state.boardUrls.push(ctrl);
      this.subscribeAutoTest(provider, ctrl);
      ids.push(integration?.id ?? null);
      testResults.push(null);
      testingStates.push(false);
    }

    state.integrationIds.set(ids);
    state.testResults.set(testResults);
    state.isTesting.set(testingStates);
  }

  private syncProviderArrayStates(selectedProviders: ProviderType[]): void {
    for (const [provider, state] of this.providerStates) {
      if (selectedProviders.includes(provider)) {
        state.boardUrls.enable({ emitEvent: false });
      } else {
        state.boardUrls.disable({ emitEvent: false });
      }
    }
  }

  onProviderSelectionChange(): void {
    const selectedProviders = this.form.controls.selectedProviders.value;
    for (const [provider, state] of this.providerStates) {
      if (selectedProviders.includes(provider)) {
        state.boardUrls.enable({ emitEvent: false });
      } else {
        this.resetProviderState(provider);
        state.boardUrls.disable({ emitEvent: false });
      }
    }
  }

  private resetProviderState(provider: ProviderType): void {
    const state = this.stateOf(provider);
    state.boardUrls.clear();
    const ctrl = this.fb.control('', [
      Validators.required,
      Validators.maxLength(BOARD_URL_MAX),
      Validators.pattern(BOARD_URL_PATTERN),
    ]);
    state.boardUrls.push(ctrl);
    this.subscribeAutoTest(provider, ctrl);
    state.integrationIds.set([null]);
    state.testResults.set([null]);
    state.isTesting.set([false]);
  }

  addBoardUrl(provider: ProviderType): void {
    const state = this.stateOf(provider);
    if (state.boardUrls.length >= MAX_BOARDS_PER_PROVIDER) return;
    const ctrl = this.fb.control('', [
      Validators.required,
      Validators.maxLength(BOARD_URL_MAX),
      Validators.pattern(BOARD_URL_PATTERN),
    ]);
    state.boardUrls.push(ctrl);
    this.subscribeAutoTest(provider, ctrl);
    state.integrationIds.update((ids) => [...ids, null]);
    state.testResults.update((results) => [...results, null]);
    state.isTesting.update((states) => [...states, false]);
  }

  removeBoardUrl(provider: ProviderType, index: number): void {
    const state = this.stateOf(provider);
    const integrationId = state.integrationIds()[index];
    if (integrationId) {
      this.integrationService.delete(this.projectId, integrationId).subscribe();
    }
    state.boardUrls.removeAt(index);
    state.integrationIds.update((ids) => ids.filter((_, i) => i !== index));
    state.testResults.update((results) => results.filter((_, i) => i !== index));
    state.isTesting.update((states) => states.filter((_, i) => i !== index));
  }

  testConnection(provider: ProviderType, index: number): void {
    const state = this.stateOf(provider);
    const integrationId = state.integrationIds()[index];
    if (!integrationId) return;

    state.isTesting.update((s) => s.map((v, i) => (i === index ? true : v)));
    state.testResults.update((r) => r.map((v, i) => (i === index ? null : v)));

    this.integrationService
      .testConnection(this.projectId, integrationId)
      .pipe(
        catchError(() => of({ success: false } as TestConnectionResult)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        state.isTesting.update((s) => s.map((v, i) => (i === index ? false : v)));
        state.testResults.update((r) => r.map((v, i) => (i === index ? result : v)));
      });
  }

  private subscribeAutoTest(provider: ProviderType, ctrl: FormControl<string>): void {
    ctrl.valueChanges
      .pipe(
        debounceTime(800),
        distinctUntilChanged(),
        filter((val) => BOARD_URL_PATTERN.test(val)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const state = this.stateOf(provider);
        const index = state.boardUrls.controls.indexOf(ctrl);
        if (index === -1) return;
        const integrationId = state.integrationIds()[index];
        if (integrationId && !state.isTesting()[index]) {
          this.testConnection(provider, index);
        }
      });
  }

  onSubmit(): void {
    const selectedProviders = this.form.controls.selectedProviders.value.filter((p) => ALLOWED_PROVIDERS.includes(p));
    selectedProviders.forEach((p) => this.stateOf(p).boardUrls.markAllAsTouched());

    const hasErrors = selectedProviders.some((p) => this.stateOf(p).boardUrls.invalid);
    if (hasErrors || selectedProviders.length === 0) return;

    this.isSaving.set(true);
    this.saveError.set(false);

    const saves$ = selectedProviders.flatMap((provider) => this.buildSaveObservables(provider));

    if (saves$.length === 0) {
      this.isSaving.set(false);
      return;
    }

    forkJoin(saves$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (results) => {
          results.forEach((integration) => this.saved.emit(integration));
          this.dirtyTracker.reset();
          this.isSaving.set(false);
        },
        error: () => {
          this.isSaving.set(false);
          this.saveError.set(true);
        },
      });
  }

  private buildSaveObservables(provider: ProviderType): Observable<ProjectIntegration>[] {
    const state = this.stateOf(provider);
    return state.boardUrls.getRawValue().map((boardUrl, index) => {
      const integrationId = state.integrationIds()[index];
      const save$ = integrationId
        ? this.integrationService.update(this.projectId, integrationId, { boardUrl })
        : this.integrationService.create(this.projectId, { providerType: provider, boardUrl });
      return save$.pipe(
        tap((savedIntegration) => {
          state.integrationIds.update((arr) => arr.map((id, i) => (i === index ? savedIntegration.id : id)));
        }),
      );
    });
  }
}
